import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CesEllipsisTooltipDirective } from '../../../../../../directives/ces-ellipsis-tooltip.directive';
import { ChartLegendManager } from '../../../../../../services/chart-legend.manager';
import { PlotlyDataPoint } from '../../../../../../types/plotly.interface';
import { CesIconComponent } from '../../../../../ui/ces-icon/ces-icon.component';
import { ACTION_BUTTON_SIZE } from '../dialogs/curve-style.constants';
import { LegendLineComponent } from './legend-line.component';

@Component({
  selector: 'app-chart-legend',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CesEllipsisTooltipDirective, CesIconComponent, DragDropModule, LegendLineComponent, TranslocoModule],
  // `relative` makes the host the containing block for the label tooltips it anchors.
  host: { class: 'block relative' },
  templateUrl: './chart-legend.component.html',
  styleUrl: './chart-legend.component.scss',
})
export class ChartLegendComponent {
  private readonly translate = inject(TranslocoService);
  private readonly legendManager = inject(ChartLegendManager);
  // Tooltip anchor: inside the fullscreen-able container but outside the scrollable list,
  // so tooltips survive native fullscreen without being clipped by the list overflow.
  protected readonly tooltipAnchor = inject(ElementRef).nativeElement as HTMLElement;
  readonly plotlyData = input.required<PlotlyDataPoint[]>();
  readonly dragFeedback = input.required<string>();
  // Locks toggle/reorder while the chart has no plotted data (the legend still
  // lists the measures, but acting on them would dirty an unloaded graph).
  readonly disabled = input<boolean>(false);

  readonly toggleVisibility = output<number>();
  readonly configure = output<number>();
  readonly drop = output<CdkDragDrop<PlotlyDataPoint[]>>();
  readonly keyDown = output<{ event: KeyboardEvent; index: number }>();

  readonly actionButtonSize = ACTION_BUTTON_SIZE;

  getHiddenLabel(): string {
    return this.translate.translate('graphs.legend.hidden');
  }

  getConfigureLabel(name: string): string {
    return `${this.translate.translate('graphs.legend.configure')} ${name}`;
  }

  getTraceKey(trace: PlotlyDataPoint, index: number): string | number {
    return this.legendManager.getTraceId(trace, index);
  }

  isTraceHidden(trace: PlotlyDataPoint): boolean {
    return trace.visible === 'legendonly';
  }
}
